import {Optional} from '@/models/TypeHelpers'
import baseURLs from '@/api/APIBaseURLs'
import axios from '@/services/axios'

export interface OrgSettings {
  [key: string]: unknown
}


export async function updateOrgSettings (settings: OrgSettings|null): Promise<void> {
  const url = baseURLs.manageAPI + '/fe_settings'
  await axios.post(url, { fe_settings: settings })
}

export async function copyOrgSettingsFromUser (userId: string): Promise<void> {
  const url = baseURLs.manageAPI + '/fe_settings'
  await axios.post(url, { user_id: userId })
}

export async function pushOrgSettingsToUser (userId: string): Promise<void> {
  const url = baseURLs.manageAPI + '/fe_settings'
  await axios.put(url, { user_id: userId })
}

const DEBOUNCED_TIMEOUT = 3 * 1000 // 2 seconds

export interface FESettings {
  org_settings: Record<number, OrgSettings>
}

// updateFESettingsInLSConfig Best effort to update fe settings in local storage config instance
// otherwise during the next initialization new settings might get overwritten
function updateFESettingsInLSConfig (feSettings: OrgSettings) {
  const conf = localStorage.getItem('appConfig')
  if (!conf) {
    return
  }
  try {
    const parsedConf = JSON.parse(conf)
    parsedConf.fe_settings = feSettings
    localStorage.setItem('appConfig', JSON.stringify(parsedConf))
  } catch (error) {
    console.error('failed to inject updated fe_settings', error)
  }

}

type configGetter = (ts: null|string, keys: string[]) => Promise<{ data: { fe_settings: OrgSettings } | null }>

// FESettingsManager is used to manage the options the user can configure for the frontend.
// Previously, we've just used localStorage to store the settings, which caused issues with data consistency and reliability.
// Now, we're making the settings management more robust by syncing settings with the backend.
// The FESettingsManager sets settings from config endpoint payload `fe_settings` field, which should be synced every time the config endpoint is refreshed.
// On the other hand, we need to save and sync (client-side) config endpoint every time the setting is updated and saved to backend.
export class FESettingsManager {
  private settings: FESettings
  // settingsUpdatedAt is the timestamp used to track changes by the local user
  private settingsUpdatedAt: Date | null = null
  // settingsSyncedAt is the timestamp used to track when the settings were last synced with the config endpoint
  private settingsSyncedAt: Date | null = null
  // currentOrgKey is the ID of the current organization
  private currentOrgKey: number
  private removedKeys: Set<string> = new Set()
  // getConfig is a function that returns the config object changed by the user based on timestamp
  // used for DI from main.ts, otherwise we encounter circular dependency
  public getConfig: configGetter = async (_ts: null|string, _keys: string[]) => {
    return { data: null}
  }

  constructor () {
    const currentOrgKey = localStorage.getItem('org_key') || 0
    const conf = localStorage.getItem('appConfig')
    this.settings = { org_settings: {} }
    this.currentOrgKey = 0
    if (conf) {
      try {
        const parsedConf = JSON.parse(conf)
        this.setSettingsFromConfig(parsedConf.fe_settings || {}, Number(currentOrgKey), true)
      } catch (e) {
        console.error('Error parsing appConfig', e)
      }
    }
  }

  // setSettingsFromConfig set settings from config endpoint payload `fe_settings` field
  setSettingsFromConfig (orgSettings: OrgSettings, orgKey: number = this.currentOrgKey, intialLoad = false) {
    const isDifferentOrg = this.currentOrgKey !== orgKey
    const syncTS = this.settingsSyncedAt?.getTime() || Infinity
    const updateTS = this.settingsUpdatedAt?.getTime() || 0
    // On the initial load: settingsSyncedAt and settingsUpdatedAt are null, since we WANT to set values from the config endpoint on the first call
    // we setting it up, so that comparison updateTS > syncTS will be false for the first time and we will set setting from /config;
    // After that if updateTS > syncTS it means that the settings were updated locally and not yet synced with remote,
    // hence we should not overwrite them with the config endpoint values
    // If we have a different org, we will overwrite the settings with the passed settings
    // In every other case we take the setting from the /config to be the primary source of the truth and fill in the missing fields from the local
    if (updateTS > syncTS && !isDifferentOrg) {
      return
    }
    this.currentOrgKey = orgKey
    this.settings.org_settings[orgKey] = orgSettings
    // For the initial load we want to set the settingsUpdatedAt to the last update time from localStorage
    if (intialLoad) {
      const updateTime = localStorage.getItem('appPartialConfigLastUpdate') || localStorage.getItem('appFullConfigLastUpdate')
      if (updateTime) {
        this.settingsUpdatedAt = new Date(updateTime)
        return
      }
    }
    this.settingsSyncedAt = new Date()
  }

  private saveSettingsDebounce: NodeJS.Timeout | null = null
  // saveSettings is a debounced function that saves the settings to the backend and refreshes the config endpoint
  // Once the setting is updated on the backend, we mark the syncedAt timestamp, after which we are going to accept the new settings from the backend
  private saveOrgSettings (orgSettings: OrgSettings){
    if (!orgSettings) {
      orgSettings = this.getOrgSettings()
    }
    if (this.saveSettingsDebounce) {
      clearTimeout(this.saveSettingsDebounce)
    }
    this.saveSettingsDebounce = setTimeout(async () => {
      try {
        // We try to refetch updated config, for cases when user updates something different before triggering the save (for example in another tab)
        const {data: newConf} = await this.getConfig(null, ['fe_settings'])
        if (newConf?.fe_settings) {
          let settings = this.getOrgSettings()
          // Take everything from the upstream config, and merge it with the current settings
          settings = Object.assign(newConf.fe_settings, settings)
          this.removedKeys.forEach(key => delete settings[key])
          this.setSettingsFromConfig(settings, this.currentOrgKey)
          orgSettings = settings
        }
      } catch (e) {
        console.error('Error fetching newer settings', e)
      }
      // Save the settings to the backend
      try {
        await updateOrgSettings(orgSettings)
        updateFESettingsInLSConfig(orgSettings)
        this.settingsSyncedAt = new Date()
        this.removedKeys.clear()
      } catch (e) {
        console.error('Error saving settings to backend', e)
      }
    }, DEBOUNCED_TIMEOUT)
  }

  public async resetCurrentOrgSettings () {
    this.settings.org_settings[this.currentOrgKey] = {}
    this.settingsUpdatedAt = new Date()
    try {
      await updateOrgSettings({})
      updateFESettingsInLSConfig({})
      this.settingsSyncedAt = new Date()
    } catch (e) {
      console.error('Error resetting current org settings', e)
    }
  }

  getOrgSettings (orgKey: number = this.currentOrgKey): OrgSettings {
    if (!this.settings.org_settings[orgKey]) {
      this.settings.org_settings[orgKey] = {}
    }
    return this.settings.org_settings[orgKey]
  }

  // setOrgSettingValue is a function that sets the value of a setting for a specific organization
  // It updates the local settings object and saves the settings to the backend
  setOrgSettingValue (key: string, value: unknown, orgKey: number = this.currentOrgKey) {
    if (!this.settings.org_settings[orgKey]) {
      this.settings.org_settings[orgKey] = {}
    }
    this.settings.org_settings[orgKey][key] = value
    this.removedKeys.delete(key)
    this.settingsUpdatedAt = new Date()
    this.saveOrgSettings(this.getOrgSettings(orgKey))
  }

  // getOrgSettingValue is a function that returns the value of a setting for a specific organization
  // It returns null if the settings are not available
  getOrgSettingValue (key: string, orgKey: number = this.currentOrgKey): Optional<string> {
    const value = this.settings?.org_settings[orgKey]?.[key] || null
    // For compatibility’s sake, we need to check the value in localStorage, and if it's found, set it in the settings, save it to backend, and return it
    // This will not be needed once all the localStorage values are migrated to the backend, and this function makes sure of that
    const storageItem = localStorage.getItem(key)
    if (!value && storageItem) {
      try {
        // const v = JSON.parse(storageItem) //TODO we can add value parsing later on, once we refactor all the places to not retry this
        // currently all the values are assumed to be strings, with properly stringified json
        this.setOrgSettingValue(key, storageItem, orgKey)
        return this.getOrgSettings(orgKey)[key] as string | null
      } catch (error) {
        console.error(`Error parsing JSON for key ${key}:`, error)
      }
      return value as string || null
    }
    return value as string || null
  }

  // To make refactoring easier, we define setItem, getItem and removeItem to make this a direct replacement for localStorage
  setItem (key: string, value: unknown) {
    this.setOrgSettingValue(key, value)
  }

  getItem (key: string): Optional<string> {
    return this.getOrgSettingValue(key)
  }

  removeItem (key: string, orgKey: number = this.currentOrgKey) {
    delete this.settings.org_settings[orgKey]?.[key]
    this.removedKeys.add(key)
    this.settingsUpdatedAt = new Date()
    this.saveOrgSettings(this.settings.org_settings[orgKey])
  }
}

export const FESettingsInstance = new FESettingsManager()
